import { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Modal, Button, Flex } from "antd"
import { MinusOutlined, PlusOutlined } from "@ant-design/icons"
import { sumCheckedIn, sumGuests } from "../checkInUtils"
import styles from "../CheckInGuestsModal/CheckInGuestsModal.module.css"

// How many of a type's checked-in guests can still be checked out.
const remainingFor = (type) => Math.max((type.checkedInCount ?? 0) - (type.checkedOutCount ?? 0), 0)

// Check-out modal for private bookings. One stepper per guest type, each picking how many of the
// *checked-in* guests of that type to check out now. onConfirm returns the new absolute per-type
// counts: [{ ...type, checkedOutCount }].
const CheckOutGuestsModal = ({ open, onClose, bookingId, clientName, guestTypes = [], loading = false, onConfirm }) => {
  const [deltas, setDeltas] = useState({})

  useEffect(() => {
    if (open) setDeltas({})
  }, [open])

  const setDelta = (key, value) => setDeltas((prev) => ({ ...prev, [key]: value }))
  const decrement = (type) => setDelta(type.key, Math.max((deltas[type.key] ?? 0) - 1, 0))
  const increment = (type) => setDelta(type.key, Math.min((deltas[type.key] ?? 0) + 1, remainingFor(type)))
  const selectAll = () => setDeltas(Object.fromEntries(guestTypes.map((type) => [type.key, remainingFor(type)])))

  const checkedIn = sumCheckedIn(guestTypes)
  const total = sumGuests(guestTypes)
  const selectedTotal = guestTypes.reduce((sum, type) => sum + (deltas[type.key] ?? 0), 0)
  const allRemaining = guestTypes.reduce((sum, type) => sum + remainingFor(type), 0)

  const handleConfirm = () =>
    onConfirm?.(guestTypes.map((type) => ({ ...type, checkedOutCount: (type.checkedOutCount ?? 0) + (deltas[type.key] ?? 0) })))

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={420} centered title="Check Out Guests">
      <Flex justify="space-between" align="center" className={styles.metaRow}>
        <Flex vertical gap={2}>
          <span className={styles.bookingId}>BOOKING ID: {bookingId}</span>
          <span className={styles.clientName}>{clientName}</span>
        </Flex>
        <span className={styles.checkedInLabel}>
          Checked-in: <strong>{checkedIn}/{total}</strong>
        </span>
      </Flex>

      <span className={styles.selectLabel}>Select guests to check out:</span>

      <Flex vertical gap={8} className={styles.typeList}>
        {guestTypes.map((type) => {
          const remaining = remainingFor(type)
          const isDone = remaining === 0
          const delta = deltas[type.key] ?? 0
          return (
            <Flex key={type.key} justify="space-between" align="center" className={styles.typeRow}>
              <span className={styles.typeChip}>{type.label}</span>
              <Flex align="center" gap={16}>
                <span className={isDone ? styles.typeCountDone : styles.typeCount}>
                  Checked-in: {type.checkedInCount}/{type.total}
                </span>
                {!isDone && (
                  <Flex align="center" gap={12}>
                    <Button shape="circle" size="small" icon={<MinusOutlined />} onClick={() => decrement(type)} disabled={delta <= 0} />
                    <span className={styles.count}>{delta}</span>
                    <Button shape="circle" size="small" icon={<PlusOutlined />} onClick={() => increment(type)} disabled={delta >= remaining} />
                  </Flex>
                )}
              </Flex>
            </Flex>
          )
        })}
      </Flex>

      <Flex justify="center" className={styles.selectAllWrap}>
        <button type="button" className={styles.selectAll} onClick={selectAll} disabled={allRemaining === 0}>
          Select all
        </button>
      </Flex>

      <Flex gap={8} justify="flex-end" className={styles.footer}>
        <Button shape="round" onClick={onClose}>
          Cancel
        </Button>
        <Button type="primary" shape="round" onClick={handleConfirm} loading={loading} disabled={selectedTotal < 1}>
          Check Out
        </Button>
      </Flex>
    </Modal>
  )
}

CheckOutGuestsModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  bookingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  clientName: PropTypes.string,
  guestTypes: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      total: PropTypes.number,
      checkedInCount: PropTypes.number,
      checkedOutCount: PropTypes.number
    })
  ),
  loading: PropTypes.bool,
  onConfirm: PropTypes.func
}

export default CheckOutGuestsModal
